import { useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  DatePicker,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '../../../ui/core';
import { CloseIcon } from '../../../ui/icons';
import { addUserNote } from '../../../redux/Users/actions';
import {
  CANCELLATION_POLICIES,
  CUSTOM_TEMPLATE_ID,
  EXCEPTION_APPROVALS,
  EXCEPTION_POLICY_START,
  NOTE_TEMPLATES,
  REFUND_METHODS,
  SUPPORT_TEAM_INITIALS,
  buildTemplateNote,
  findPriorExceptionNotes,
  findTemplate,
  missingTemplateFields,
} from './noteTemplates';

// Every dropdown starts empty and must be picked on purpose, so a note never records a
// refund method, exception or agent nobody actually chose. The date is the exception:
// it defaults to today (still changeable, still required).
const emptyFields = () => ({
  policy: '',
  refundMethod: '',
  approval: '',
  date: moment(),
  initials: '',
});

function AddUserNoteDialog({ isOpen, onClose, userData, onNoteAdded, existingNotes, notesStatus = 'done' }) {
  const dispatch = useDispatch();

  const priorExceptions = findPriorExceptionNotes(existingNotes);

  const [templateId, setTemplateId] = useState('');
  const [fields, setFields] = useState(emptyFields);
  // Hand edits to a generated note. `null` means "show the generated text".
  const [editedBody, setEditedBody] = useState(null);
  const [customBody, setCustomBody] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const isCustom = templateId === CUSTOM_TEMPLATE_ID;
  const selectedTemplate = findTemplate(templateId);
  const missing = selectedTemplate ? missingTemplateFields(templateId, fields) : [];
  const isComplete = Boolean(selectedTemplate) && missing.length === 0;
  const generatedBody = isComplete ? buildTemplateNote(templateId, fields) : '';

  let noteBody = '';
  if (isCustom) {
    noteBody = customBody;
  } else if (isComplete) {
    noteBody = editedBody ?? generatedBody;
  }

  const canSubmit = (isCustom || isComplete) && Boolean(noteBody.trim());
  const isException = selectedTemplate?.fields.includes('approval');
  // The prior-exception check reads the notes list; until it has loaded, "no warning"
  // would wrongly look like "no prior exception", so say the check didn't run instead.
  const notesChecked = notesStatus === 'done';
  const showPriorExceptionWarning = isException && notesChecked && priorExceptions.length > 0;

  function handleTemplateChange(nextId) {
    setTemplateId(nextId);
    setFields(emptyFields());
    setEditedBody(null);
    setCustomBody('');
  }

  function updateField(name, value) {
    setFields((current) => ({ ...current, [name]: value }));
    // A dropdown change rebuilds the note, replacing any hand edits.
    setEditedBody(null);
  }

  function handleClose() {
    onClose();
    // Delay the reset so the text doesn't visibly clear while the dialog closes.
    setTimeout(() => {
      handleTemplateChange('');
      setSubmitError(null);
    }, 300);
  }

  async function handleSubmit() {
    if (!canSubmit || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await addUserNote({
        userId: userData.id,
        body: noteBody.trim(),
      })(dispatch);

      if (result.success) {
        if (onNoteAdded) {
          await onNoteAdded();
        }
        handleClose();
      } else {
        setSubmitError('Could not save note. Please try again.');
      }
    } catch (error) {
      setSubmitError('Could not save note. Please try again.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  const select = (name, label, options, sx) => (
    <TextField
      select
      required
      label={label}
      value={fields[name]}
      onChange={(e) => updateField(name, e.target.value)}
      sx={sx}
    >
      {options.map((o) => (
        <MenuItem key={o.id} value={o.id}>
          {o.label}
        </MenuItem>
      ))}
    </TextField>
  );

  let noteHelper = ' ';
  if (selectedTemplate && !isComplete) {
    noteHelper = `Select ${missing.map((m) => m.label).join(', ')} to build the note.`;
  } else if (isComplete) {
    noteHelper = 'Edit freely before saving, e.g. add the trip ID.';
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} sx={{ userSelect: 'none' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" padding="32px 32px 0 32px">
        <DialogTitle sx={{ padding: 0 }}>Add Note</DialogTitle>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent>
        <Stack width="460px" maxWidth="100%" paddingTop="8px" spacing={2}>
          <TextField
            select
            required
            label="Note type"
            value={templateId}
            onChange={(e) => handleTemplateChange(e.target.value)}
            fullWidth
          >
            {NOTE_TEMPLATES.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                <Box>
                  <Typography sx={{ fontSize: 14 }}>{t.label}</Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{t.description}</Typography>
                </Box>
              </MenuItem>
            ))}
            <MenuItem value={CUSTOM_TEMPLATE_ID}>Custom note</MenuItem>
          </TextField>

          {isException && (
            <>
              {select('policy', 'Cancellation policy', CANCELLATION_POLICIES, { width: '100%' })}
              <Stack direction="row" spacing={1.5}>
                {select('refundMethod', 'Refund to', REFUND_METHODS, { flex: 1 })}
                {select('approval', 'Exception', EXCEPTION_APPROVALS, { flex: 1 })}
              </Stack>
            </>
          )}

          {selectedTemplate && (
            <Stack direction="row" spacing={1.5}>
              <Box sx={{ flex: 1 }}>
                <DatePicker
                  fullWidth
                  moment
                  label="Date *"
                  value={fields.date}
                  // Exceptions only exist from the 2026 policy start; nothing earlier is pickable.
                  minDate={EXCEPTION_POLICY_START}
                  onChange={(value) => {
                    // Half-typed or pre-2026 dates count as not selected.
                    const valid = value && value.isValid() && !value.isBefore(EXCEPTION_POLICY_START, 'day');
                    updateField('date', valid ? value : null);
                  }}
                />
              </Box>
              {select(
                'initials',
                'Initials',
                SUPPORT_TEAM_INITIALS.map((code) => ({ id: code, label: code })),
                { width: 120 },
              )}
            </Stack>
          )}

          {isException && notesStatus === 'loading' && (
            <Alert severity="info">Checking this user&rsquo;s notes for a prior exception&hellip;</Alert>
          )}

          {isException && notesStatus === 'failed' && (
            <Alert severity="warning">
              Could not check for a prior exception because the notes didn&rsquo;t load. Check Support notes before
              giving an exception.
            </Alert>
          )}

          {showPriorExceptionWarning && (
            <Alert severity="warning">
              This user already had a one-time exception in 2026, so another one needs Noelle&rsquo;s approval:
              {priorExceptions.slice(0, 3).map((note) => (
                <Box key={note.id} component="span" sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}>
                  &ldquo;{note.body}&rdquo;
                </Box>
              ))}
            </Alert>
          )}

          {templateId && (
            <TextField
              label="Note"
              placeholder="e.g. CP One Time Excep 48HRS Cash ND 11/03/2026 - First exception"
              value={noteBody}
              onChange={(e) => (isCustom ? setCustomBody(e.target.value) : setEditedBody(e.target.value))}
              disabled={!isCustom && !isComplete}
              multiline
              rows={4}
              autoFocus={isCustom}
              helperText={noteHelper}
              sx={{ width: '100% !important' }}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
        <Box sx={{ width: '100%' }}>
          {submitError && (
            <Alert severity="error" sx={{ marginBottom: '12px' }}>
              {submitError}
            </Alert>
          )}
          <Button
            type="button"
            variant="contained"
            fullWidth
            color="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save Note'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default AddUserNoteDialog;
